import { startTransition, useState, useRef } from "react";
import { convertDate } from "@Services/luxonHelper";

type FieldRules = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  minRange?: number;
  maxRange?: number;
  maxFieldRange?: number;
  minFieldRange?: number;
  selectionScope?: "futureDateTime" | "pastDateTime";
  minNoOfFiles?: number;
  maxNoOfFiles?: number;
};

const useFormValidator = (initialRules = {}) => {
  const [error, setError] = useState<any>({});
  const rulesRef = useRef<any>(initialRules);

  const updateRules = (newRules: any) => {
    rulesRef.current = { ...rulesRef.current, ...newRules };
  };

  // Validation errors are non-urgent UI feedback — wrap setError in startTransition
  // so per-keystroke validation doesn't block paint by triggering a sync parent re-render.
  const setFieldError = (fieldName: string, message: string) => {
    startTransition(() => {
      setError((prevState: Record<string, string>) => ({
        ...prevState,
        [fieldName]: message,
      }));
    });
  };

  const clearFieldError = (fieldName: string) => {
    startTransition(() => {
      setError((prevState: Record<string, string>) => ({
        ...prevState,
        [fieldName]: "",
      }));
    });
  };

  const checkRequired = (name: string, value: any, rules: FieldRules) => {
    if (!rules.required) return true;

    const isEmptyArray = Array.isArray(value) && value.length === 0;
    const isEmptyValue = !value && value !== 0; // allow 0 as valid

    if (isEmptyArray || isEmptyValue) {
      setFieldError(name, "This field is required");
      return false;
    }
    return true;
  };

  const checkStringBounds = (name: string, value: any, rules: FieldRules) => {
    if (typeof value !== "string") return true;

    if (rules.minLength && value.length < rules.minLength) {
      setFieldError(name, `Minimum ${rules.minLength} characters required`);
      return false;
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      setFieldError(name, `Maximum ${rules.maxLength} characters allowed`);
      return false;
    }

    return true;
  };

  const checkNumberBounds = (name: string, value: any, rules: FieldRules) => {
    if (typeof value !== "number") return true;

    const num = Number(value);

    if (rules.minRange && num < rules.minRange) {
      setFieldError(name, `Minimum value should be ${rules.minRange}`);
      return false;
    }

    if (rules.maxRange && num > rules.maxRange) {
      setFieldError(name, `Maximum value should be ${rules.maxRange}`);
      return false;
    }

    if (rules.maxFieldRange && num > rules.maxFieldRange) {
      setFieldError(name, `Maximum date can be ${convertDate(rules.maxFieldRange, "dateforProjectDashbaord")}`);
      return false;
    }

    if (rules.minFieldRange && num < rules.minFieldRange) {
      setFieldError(name, `Minimum date can be ${convertDate(rules.minFieldRange, "dateforProjectDashbaord")}`);
      return false;
    }

    const scopeError = getDateTimeScopeError(value, rules.selectionScope);
    if (scopeError) {
      setFieldError(name, scopeError);
      return false;
    }

    return true;
  };

  const getDateTimeScopeError = (value: any, scope?: FieldRules["selectionScope"]) => {
    if (!scope) return "";

    const currentDate = new Date();
    const selectedDate = new Date(value);

    if (scope === "futureDateTime" && selectedDate <= currentDate) {
      return "Please select a future date and time";
    }

    if (scope === "pastDateTime" && selectedDate >= currentDate) {
      return "Please select a past date and time";
    }

    return "";
  };

  const checkFileCounts = (name: string, value: any, rules: FieldRules) => {
    const length = value?.length ?? 0;

    if (rules.minNoOfFiles && length < rules.minNoOfFiles) {
      setFieldError(name, `Minimum ${rules.minNoOfFiles} files required`);
      return false;
    }

    if (rules.maxNoOfFiles && length > rules.maxNoOfFiles) {
      setFieldError(name, `Maximum ${rules.maxNoOfFiles} files allowed`);
      return false;
    }

    return true;
  };

  const validate = ({ name, value }: { name: string; value: any }) => {
    const fieldRules = rulesRef.current[name] || {};
    rulesRef.current = {
      ...rulesRef.current,
      [name]: {
        ...rulesRef.current[name],
        value,
      },
    };
    if (!checkRequired(name, value, fieldRules)) return false;
    if (!checkStringBounds(name, value, fieldRules)) return false;
    if (!checkNumberBounds(name, value, fieldRules)) return false;
    if (!checkFileCounts(name, value, fieldRules)) return false;

    clearFieldError(name);
    return true;
  };

  function validateAll() {
    let isValid = true;
    for (const name of Object.keys(rulesRef.current)) {
      const res = validate({ name, value: rulesRef.current[name].value });
      if (!res) {
        isValid = false;
      }
    }
    return isValid;
  }

  return { validate, error, updateRules, validateAll };
};

export default useFormValidator;
